import styled from '@emotion/styled';
import { Skeleton, tokens } from '@sunrun/experience-ui-components';
import { useEffect, useState } from 'react';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import { HorizontalWidgetScrollerContainer } from 'components/atoms/HorizontalWidgetScrollerContainer';
import { VideoModal } from 'components/molecules/VideoModal';
import { useHybridToken } from 'providers/Auth';

type ReelsApiVideo = {
  id: string;
  title: string;
  description?: string;
  url: string;
  isHls?: boolean;
  thumbnailUrl?: string;
  tags?: string[];
};

type Stream = {
  id?: string;
  title: string;
  description: string;
  url: string;
  isHls?: boolean;
  thumbnailUrl?: string;
  tags?: string[];
};

function mapApiVideoToStream(v: ReelsApiVideo): Stream {
  return {
    id: v.id,
    title: v.title ?? '',
    description: v.description ?? '',
    url: v.url ?? '',
    isHls: v.isHls ?? /\.m3u8(\?|$)/i.test(v.url ?? ''),
    thumbnailUrl: v.thumbnailUrl,
    tags: v.tags ?? [],
  };
}

function mergeReelsVideos(
  sunrun: ReelsApiVideo[],
  forYou: ReelsApiVideo[],
): ReelsApiVideo[] {
  const seen = new Set<string>();
  const out: ReelsApiVideo[] = [];
  for (const v of sunrun) {
    if (v.id) {
      if (seen.has(v.id)) continue;
      seen.add(v.id);
    }
    out.push(v);
  }
  for (const v of forYou) {
    if (v.id) {
      if (seen.has(v.id)) continue;
      seen.add(v.id);
    }
    out.push(v);
  }
  return out;
}

const STOCK_SUNRUN_THUMBNAIL = '/Sunrun_Logo.svg';

const REELS_API_BASE_URLS: Record<string, string> = {
  production: 'https://mu34d12k29.execute-api.us-west-2.amazonaws.com',
  staging: 'https://0i6lllmky3.execute-api.us-west-2.amazonaws.com',
};

const useStreams = () => {
  const { hybridToken } = useHybridToken();
  const [streams, setStreams] = useState<Stream[]>([]);
  const [isStreamsLoading, setIsStreamsLoading] = useState(true);

  useEffect(() => {
    if (!hybridToken) {
      setIsStreamsLoading(false);
      setStreams([]);
      return;
    }
    const env = getEnvironment();
    const baseUrl = REELS_API_BASE_URLS[env] ?? REELS_API_BASE_URLS.staging;
    const url = `${baseUrl}/training-reels-videos`;
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${hybridToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          return Promise.reject(new Error(`HTTP ${res.status}`));
        }
        return res.json();
      })
      .then(
        (data: {
          sunrunVideos?: ReelsApiVideo[];
          forYouVideos?: ReelsApiVideo[];
        }) => {
          const sunrun = data.sunrunVideos ?? [];
          const forYou = data.forYouVideos ?? [];
          const merged = mergeReelsVideos(sunrun, forYou);
          setStreams(merged.map(mapApiVideoToStream));
        },
      )
      .catch(() => {
        setStreams([]);
      })
      .finally(() => {
        setIsStreamsLoading(false);
      });
  }, [hybridToken]);

  return { streams, isStreamsLoading };
};

const StreamsWidget = () => {
  const { streams, isStreamsLoading } = useStreams();
  const [modalIndex, setModalIndex] = useState<number | null>(null);

  const modalVideo =
    modalIndex != null && streams[modalIndex] ? streams[modalIndex] : null;

  return (
    <>
      <HorizontalWidgetScrollerContainer>
        {streams.length > 0 &&
          streams.map((stream, index) => (
            <StreamItem
              key={stream.id ?? `${stream.title}-${index}`}
              stream={stream}
              onPlay={() => setModalIndex(index)}
            />
          ))}
        {(isStreamsLoading || streams.length === 0) &&
          [1, 2, 3, 4, 5, 6].map((item) => <StyledSkeleton key={item} />)}
      </HorizontalWidgetScrollerContainer>
      {modalVideo && modalIndex != null && (
        <VideoModal
          video={modalVideo}
          onClose={() => setModalIndex(null)}
          navigation={
            streams.length > 1
              ? {
                  canGoPrevious: modalIndex > 0,
                  canGoNext: modalIndex < streams.length - 1,
                  onPrevious: () =>
                    setModalIndex((i) => (i != null && i > 0 ? i - 1 : i)),
                  onNext: () =>
                    setModalIndex((i) =>
                      i != null && i < streams.length - 1 ? i + 1 : i,
                    ),
                }
              : undefined
          }
        />
      )}
    </>
  );
};

type StreamItemProps = {
  stream: Stream;
  onPlay: () => void;
};

const StreamItem = ({ stream, onPlay }: StreamItemProps) => {
  const isHls = stream.isHls ?? /\.m3u8(\?|$)/i.test(stream.url);
  const showPlaceholder = isHls && !stream.thumbnailUrl;
  const thumbnailUrl = stream.thumbnailUrl ?? STOCK_SUNRUN_THUMBNAIL;
  const tags = stream.tags ?? [];

  return (
    <StreamItemContainer onClick={onPlay}>
      <VideoThumbnailWrapper>
        {showPlaceholder ? (
          <ThumbnailPlaceholder $thumb={STOCK_SUNRUN_THUMBNAIL} />
        ) : (
          <video
            src={stream.url}
            poster={thumbnailUrl}
            muted
            preload="metadata"
            playsInline
          />
        )}
        <CardTextOverlay>
          <CardTitle>{stream.title}</CardTitle>
          {stream.description ? (
            <CardDescription>{stream.description}</CardDescription>
          ) : null}
          {tags.length > 0 ? (
            <CardTags>{tags.map((tag) => `#${tag}`).join(' ')}</CardTags>
          ) : null}
        </CardTextOverlay>
        <PlayButtonOverlay aria-label="Play video">
          <PlayIcon />
        </PlayButtonOverlay>
      </VideoThumbnailWrapper>
    </StreamItemContainer>
  );
};

const StreamItemContainer = styled.div`
  width: 214px;
  min-width: 214px;
  background-color: ${tokens.WHITE};
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
`;

const ThumbnailPlaceholder = styled.div<{ $thumb: string }>`
  width: 100%;
  height: 100%;
  background-color: ${tokens.TINTS_OFF_WHITE_20};
  background-image: url(${({ $thumb }) => $thumb});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 8px;
`;

const VideoThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 17;
  background-color: ${tokens.TINTS_OFF_WHITE_20};
  overflow: hidden;

  video {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const CardTextOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 8px 8px;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.5) 60%,
    transparent 100%
  );
  border-radius: 0 0 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1;
  pointer-events: none;
`;

const CardTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  color: ${tokens.WHITE};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardDescription = styled.span`
  font-size: 10px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.95);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardTags = styled.span`
  font-size: 10px;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.9);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PlayButtonOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  z-index: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.45);
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 20px solid rgba(255, 255, 255, 0.95);
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  margin-left: 6px;
  border-radius: 2px;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 214px;
  min-width: 214px;
  height: 100%;
`;

export { StreamsWidget };
export type { Stream };
